import React from 'react';
import { useConstruction } from '../../context/ConstructionContext';
import { formatMoney } from '../../utils/helpers';
import { formatIndex } from '../../construction/money';
import DataTable from '../DataTable';
import Badge from '../common/Badge';
import ConstructionShell, { IndexBadge, Panel, indexTone } from './ConstructionShell';
import { Kpi } from './ConstructionProgress';
import { t } from '../../i18n';

const CostsContent = ({ summary }) => {
  const { can, convert, displayCurrency, setProjectCurrency } = useConstruction();
  const money = (amount) => formatMoney(Math.round(convert(amount || 0, summary.currency, displayCurrency)), displayCurrency);
  const p = summary.evm.project;
  const { actualCost } = summary;

  const kpis = [
    { label: 'BAC (presupuesto obra)', value: money(p.bac) },
    { label: 'PV (valor planificado)', value: money(p.pv) },
    { label: 'EV (valor ganado)', value: money(p.ev) },
    { label: 'AC (costo real)', value: money(p.ac) },
    { label: 'CV (EV − AC)', value: money(p.cv), tone: p.cv < 0 ? 'danger' : undefined },
    { label: 'SV (EV − PV)', value: money(p.sv), tone: p.sv < 0 ? 'warning' : undefined },
    { label: 'CPI (EV / AC)', value: formatIndex(p.cpi), tone: indexTone(p.cpi) },
    { label: 'SPI (EV / PV)', value: formatIndex(p.spi), tone: indexTone(p.spi) },
    { label: 'EAC (estimado al término)', value: money(p.eac), tone: p.eac > p.bac ? 'danger' : undefined },
    { label: 'ETC (por gastar)', value: money(p.etc) },
    { label: 'VAC (BAC − EAC)', value: money(p.vac), tone: p.vac < 0 ? 'danger' : undefined },
    { label: 'Comprometido (OC + contratos)', value: money(actualCost.committed) }
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        {kpis.map((kpi) => <Kpi key={kpi.label} label={kpi.label} value={kpi.value} tone={kpi.tone === 'success' || kpi.tone === 'neutral' ? undefined : kpi.tone} />)}
      </div>

      <Panel
        title="Earned Value por partida"
        description="EV = % avance × BAC. AC desde OC del Área Construcción (por paquete WBS) y estados de pago aprobados."
        actions={
          can('costs.editBudget') ? (
            <label className="flex items-center gap-2 text-xs text-neutral-600">
              {t('Moneda del presupuesto de obra')}
              <select className="h-7 px-2 border border-neutral-300 rounded text-xs" value={summary.currency} onChange={(event) => setProjectCurrency(summary.parkId, event.target.value)}>
                <option value="USD">USD</option>
                <option value="CLP">CLP</option>
              </select>
            </label>
          ) : null
        }
      >
        <DataTable
          data={summary.evm.rows}
          columns={[
            { key: 'name', label: 'Partida', render: (value, row) => <span style={{ paddingLeft: row.depth * 14 }} className={row.isLeaf ? '' : 'font-semibold'}><span className="text-neutral-400 mr-1.5">{row.code}</span>{value}</span> },
            { key: 'bac', label: 'BAC', align: 'right', sortable: true, render: money },
            { key: 'pv', label: 'PV', align: 'right', render: money },
            { key: 'ev', label: 'EV', align: 'right', render: money },
            { key: 'ac', label: 'AC', align: 'right', render: money },
            { key: 'cv', label: 'CV', align: 'right', render: (value) => <span className={value < 0 ? 'text-danger-700' : ''}>{money(value)}</span> },
            { key: 'cpi', label: 'CPI', align: 'right', sortable: true, render: (value) => <IndexBadge label="" value={value} /> },
            { key: 'spi', label: 'SPI', align: 'right', sortable: true, render: (value) => <IndexBadge label="" value={value} /> },
            { key: 'eac', label: 'EAC', align: 'right', render: money }
          ]}
          searchPlaceholder="Buscar partida..."
        />
        {actualCost.unassigned > 0 && (
          <p className="mt-3 text-xs text-warning-700 bg-warning-50 border border-warning-100 rounded px-2.5 py-1.5">
            {t('{0} de costo real no tiene paquete WBS enlazado a una partida: se suma al AC del proyecto pero no al de una partida.', money(actualCost.unassigned))}
          </p>
        )}
      </Panel>

      <Panel title="Origen del costo real (AC)" description="Enganche con Abastecimiento: OC facturadas o recibidas imputadas al Área Construcción / su CECO, más estados de pago aprobados.">
        <DataTable
          data={actualCost.sources.map((source) => ({ ...source, typeLabel: source.type === 'oc' ? 'Orden de compra' : 'Estado de pago' }))}
          columns={[
            { key: 'typeLabel', label: 'Origen', render: (value, row) => <Badge tone={row.type === 'oc' ? 'info' : 'neutral'}>{t(value)}</Badge> },
            { key: 'id', label: 'Documento', sortable: true },
            { key: 'label', label: 'Proveedor / contratista', sortable: true },
            { key: 'date', label: 'Fecha', sortable: true, render: (value) => value || '—' },
            { key: 'amount', label: 'Monto', align: 'right', sortable: true, render: money }
          ]}
          searchPlaceholder="Buscar documento..."
          emptyTitle="Sin costo real imputado"
          emptyDescription="Las OC del Área Construcción aparecen aquí cuando se reciben o facturan."
        />
      </Panel>
    </div>
  );
};

const ConstructionCosts = () => (
  <ConstructionShell title="Control de costos de obra (EVM)" description="Earned Value Management: PV, EV, AC, CV, SV, CPI, SPI, EAC y ETC por partida y proyecto." requireItems>
    {(summary) => <CostsContent summary={summary} />}
  </ConstructionShell>
);

export default ConstructionCosts;
